import React, { useState } from 'react';
import { SETUP_CHECKLIST } from '../../data/constants';
import ChecklistItem from './ChecklistItem';
import ProgressBar from '../common/ProgressBar';
import SafeIcon from '../../common/SafeIcon';
import { FiChevronDown, FiChevronUp, FiCheck, FiInfo } from 'react-icons/fi';
import Confetti from '../common/Confetti';

const SetupTab = ({ shop, progress, setProgress }) => {
  const [openSections, setOpenSections] = useState(
    SETUP_CHECKLIST.reduce((acc, section) => ({ ...acc, [section.id]: true }), {})
  );
  const [showConfetti, setShowConfetti] = useState(false);

  if (!shop) return null;

  // Filter and Dynamically Update Checklist Content
  const dynamicChecklist = SETUP_CHECKLIST
    .filter(section => {
      if (shop.type === 'etsy' && section.id === 'shopify-setup') return false;
      if (shop.type === 'shopify' && section.id === 'etsy-setup') return false;
      return true;
    })
    .map(section => {
      // Inject dynamic shop name into Brand Identity section
      if (section.id === 'brand-identity') {
        return {
          ...section,
          items: section.items.map(item => {
            if (item.id === 'bi-1') {
              return {
                ...item,
                title: `Shop Name: ${shop.name}`,
                description: `Finalize "${shop.name}". Check it's available on Etsy, Shopify, and all social platforms simultaneously.`
              };
            }
            return item;
          })
        };
      }
      return section;
    });

  const toggleSection = (id) => {
    setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleItem = (sectionId, itemId) => {
    const sectionProgress = progress[sectionId] || {};
    const currentData = sectionProgress[itemId];
    
    const isCompleted = typeof currentData === 'object' ? currentData.completed : !!currentData;
    const newItemData = typeof currentData === 'object' 
      ? { ...currentData, completed: !isCompleted } 
      : { completed: !isCompleted, notes: '', imageUrl: '' };

    const newSectionProgress = { ...sectionProgress, [itemId]: newItemData };
    const newProgress = { ...progress, [sectionId]: newSectionProgress };

    // Confetti logic
    const section = dynamicChecklist.find(s => s.id === sectionId);
    if (section) {
      const wasCompleted = section.items.every(i => {
        const d = sectionProgress[i.id];
        return typeof d === 'object' ? d.completed : !!d;
      });
      const isNowCompleted = section.items.every(i => {
        const d = newSectionProgress[i.id];
        return typeof d === 'object' ? d.completed : !!d;
      });

      if (!wasCompleted && isNowCompleted) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
      }
    }
    setProgress(newProgress);
  };

  const updateItemDetails = (sectionId, itemId, details) => {
    const sectionProgress = progress[sectionId] || {};
    const newSectionProgress = { ...sectionProgress, [itemId]: details };
    setProgress({ ...progress, [sectionId]: newSectionProgress });
  };

  const totalItems = dynamicChecklist.reduce((acc, section) => acc + section.items.length, 0);
  const completedItems = dynamicChecklist.reduce((acc, section) => {
    const sectionProgress = progress[section.id] || {};
    return acc + Object.values(sectionProgress).filter(d => typeof d === 'object' ? d.completed : !!d).length;
  }, 0);

  return (
    <div className="max-w-4xl mx-auto pb-12 animate-fade-in">
      <Confetti trigger={showConfetti} />
      
      <div className="mb-10 bg-white p-6 md:p-8 rounded-3xl shadow-sm border border-cream-dark">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold text-espresso mb-1">{shop.name}</h2>
            <p className="text-espresso-light/60 flex items-center gap-2">
              <span className="uppercase text-[10px] font-bold tracking-widest px-2 py-0.5 bg-cream-dark rounded">{shop.type} Platform</span> 
              Strategic Setup Guide
            </p>
          </div>
          <div className="bg-sage/10 text-sage px-4 py-2 rounded-2xl font-bold text-sm">
            {Math.round((completedItems / totalItems) * 100)}% Complete
          </div>
        </div>
        <ProgressBar value={completedItems} max={totalItems} label="Overall Progress" />
      </div>

      <div className="space-y-4">
        {dynamicChecklist.map((section, index) => {
          const sectionProgress = progress[section.id] || {};
          const sectionCompletedCount = section.items.filter(item => {
            const d = sectionProgress[item.id];
            return typeof d === 'object' ? d.completed : !!d;
          }).length;
          const isFullComplete = sectionCompletedCount === section.items.length;

          return (
            <div key={section.id} className="bg-white rounded-2xl shadow-sm border border-cream-dark overflow-hidden transition-all">
              <button 
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between p-5 bg-white hover:bg-cream/20 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold shadow-sm ${isFullComplete ? 'bg-sage text-white' : 'bg-cream text-espresso-light border border-cream-dark'}`}>
                    {isFullComplete ? <SafeIcon icon={FiCheck} /> : index + 1}
                  </div>
                  <h3 className={`text-xl font-bold ${isFullComplete ? 'text-espresso-light/60' : 'text-espresso'}`}>
                    {section.title}
                  </h3>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-[10px] font-bold text-espresso-light/50 uppercase tracking-widest bg-cream-dark/30 px-2 py-1 rounded-lg">
                    {sectionCompletedCount} / {section.items.length}
                  </span>
                  <SafeIcon icon={openSections[section.id] ? FiChevronUp : FiChevronDown} className="text-espresso-light/40 w-4 h-4" />
                </div>
              </button>

              {openSections[section.id] && (
                <div className="p-5 pt-0 border-t border-cream-dark/30 bg-cream/5">
                  <div className="space-y-3 mt-4">
                    {section.items.map(item => (
                      <ChecklistItem 
                        key={item.id}
                        item={item}
                        progressData={sectionProgress[item.id]}
                        onToggle={() => toggleItem(section.id, item.id)}
                        onUpdateDetails={(details) => updateItemDetails(section.id, item.id, details)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SetupTab;